import { useAgenticAI } from "@/hooks/agenticAI/useAgenticAI";

import { LokasiAIContent, LokasiAIForm } from "@lokasi-ai-web-sdk/app";
import { twMerge } from "tailwind-merge";

import AgenticAIComposer from "./AgenticAIComposer";
import AgenticAIConversation from "./AgenticAIConversation";
import AgenticAIMap from "./AgenticAIMap";
import AgenticAISidebar from "./AgenticAISidebar";
import AgenticAIWelcome from "./AgenticAIWelcome";
import {
  AGENTIC_AI_DESCRIPTION,
  AGENTIC_AI_HEADING,
  AGENTIC_AI_SIDEBAR_TITLE
} from "./constants";
import SidebarToggle from "./SidebarToggle";
import SuggestedPrompts from "./SuggestedPrompts";

const AgenticAI = () => {
  const {
    activeChatId,
    activeConversation,
    chatHistory,
    composerPlaceholder,
    error,
    hasConversation,
    isBusy,
    isMapFullScreen,
    isSidebarOpen,
    suggestedPrompts,
    handleNewChat,
    handleSelectChat,
    handleSelectSuggestedPrompt,
    handleToggleMapFullScreen,
    handleToggleSidebar
  } = useAgenticAI();

  return (
    <div className="flex h-full bg-white-smoke">
      {isSidebarOpen && !isMapFullScreen && (
        <AgenticAISidebar
          title={AGENTIC_AI_SIDEBAR_TITLE}
          chats={chatHistory}
          activeChatId={activeChatId}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
        />
      )}

      {!isMapFullScreen && (
        <div
          className={twMerge(
            "flex min-w-0 flex-1 flex-col",
            // Fixed once the map is up, so every pixel the sidebar gives back
            // goes to the map instead of stretching the conversation.
            activeConversation &&
              "border-r border-gainsboro lg:w-[520px] lg:flex-none"
          )}
        >
          {/* Sits above the scroller, not inside it, so the toggle stays put
        while the conversation scrolls. */}
          <div className="flex shrink-0 items-center px-4 py-2">
            <SidebarToggle
              isSidebarOpen={isSidebarOpen}
              onToggle={handleToggleSidebar}
            />
          </div>

          <div
            className={twMerge(
              "flex min-h-0 flex-1 flex-col px-6 pb-10",
              !activeConversation && "overflow-y-auto"
            )}
          >
            <div
              className={twMerge(
                "mx-auto flex w-full max-w-[720px] flex-1 flex-col gap-6 py-6",
                hasConversation ? "justify-start" : "justify-center",
                activeConversation && "min-h-0"
              )}
            >
              {activeConversation ? (
                <div className="min-h-0 flex-1">
                  <AgenticAIConversation
                    startedAtLabel={activeConversation.startedAtLabel}
                    messages={activeConversation.messages}
                  />
                </div>
              ) : hasConversation ? (
                <LokasiAIContent className="flex-1" />
              ) : (
                <AgenticAIWelcome
                  heading={AGENTIC_AI_HEADING}
                  description={AGENTIC_AI_DESCRIPTION}
                />
              )}

              {/* `!h-auto` overrides the SDK form's own `h-full` so the composer
            hugs its content instead of stretching the column. */}
              <LokasiAIForm className="!h-auto">
                {({ prompt, setPrompt }) => (
                  <AgenticAIComposer
                    value={prompt}
                    onChange={setPrompt}
                    disabled={isBusy}
                    placeholder={composerPlaceholder}
                  />
                )}
              </LokasiAIForm>

              {error && (
                <p role="alert" className="text-xs text-cinnabar">
                  {error}
                </p>
              )}

              {!hasConversation && (
                <SuggestedPrompts
                  prompts={suggestedPrompts}
                  disabled={isBusy}
                  onSelect={handleSelectSuggestedPrompt}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {activeConversation && (
        <div className="relative hidden min-w-0 flex-1 lg:block">
          <AgenticAIMap
            center={activeConversation.mapView.center}
            zoom={activeConversation.mapView.zoom}
            isFullScreen={isMapFullScreen}
            onToggleFullScreen={handleToggleMapFullScreen}
          />
        </div>
      )}
    </div>
  );
};

export default AgenticAI;
